import React, { Fragment, useCallback, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Spinner } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchVatSummary } from "../../../api/accounting";
import { formatTZS } from "../../../utils/format";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runLoad } from "../Common/credoraToast";
import AccountingReportFilters from "../Accounting/AccountingReportFilters";
import { accountingReportBreadcrumbs } from "../Accounting/accountingReportShell";
import { useAccountingReportFilters } from "../Accounting/useAccountingReportFilters";

const VatSummaryReportPage = () => {
  const crumbs = accountingReportBreadcrumbs({ reportMode: true, title: "VAT Summary" });
  const filters = useAccountingReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!filters.branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchVatSummary(filters.reportParams()).then(setData), {
      errorMessage: "Failed to load VAT summary.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load VAT summary.")))
      .finally(() => setLoading(false));
  }, [filters.branchId, filters.reportParams]);

  const columns = [
    { name: "Code", selector: (r) => r.code, width: "90px" },
    { name: "Tax", selector: (r) => r.name, grow: 2 },
    { name: "Type", selector: (r) => r.tax_type, width: "90px" },
    { name: "Rate %", selector: (r) => r.rate_pct, width: "80px" },
    { name: "Base", cell: (r) => formatTZS(r.base_amount), right: true, width: "130px" },
    { name: "Tax amount", cell: (r) => formatTZS(r.tax_amount), right: true, width: "130px" },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className="container-fluid credora-finance-reports pb-5 mb-3">
        {error && <Alert color="danger">{error}</Alert>}
        <AccountingReportFilters {...filters} onApply={load} loading={loading} />
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">VAT summary</h5>
              <small className="text-muted">
                {data.date_from} – {data.date_to}
                {data.branch_name ? ` · ${data.branch_name}` : ""}
              </small>
            </div>
            <div className="card-body p-0">
              <DataTable columns={columns} data={data.rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            </div>
            <div className="card-footer d-flex justify-content-end gap-4">
              <strong>Total base: {formatTZS(data.total_base)}</strong>
              <strong>Total tax: {formatTZS(data.total_tax)}</strong>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
};

export default VatSummaryReportPage;
